// src/features/portfolio/portfolioSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as portfolioService from '../../services/portfolioService';

// Async thunks for CRUD operations
export const fetchPortfolioDataAsync = createAsyncThunk(
  'portfolio/fetchData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await portfolioService.fetchPortfolioData();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPortfolioItemAsync = createAsyncThunk(
  'portfolio/createItem',
  async (portfolioData, { rejectWithValue }) => {
    try {
      const response = await portfolioService.createPortfolioItem(portfolioData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePortfolioItemAsync = createAsyncThunk(
  'portfolio/updateItem',
  async ({ id, data, changedBy }, { rejectWithValue }) => {
    try {
      const response = await portfolioService.updatePortfolioItem(id, data, changedBy);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePortfolioItemAsync = createAsyncThunk(
  'portfolio/deleteItem',
  async (id, { rejectWithValue }) => {
    try {
      const response = await portfolioService.deletePortfolioItem(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  items: [],
  rowCount: 0,
  amountInvested: 0,
  loading: false,
  error: null,
  lastUpdated: null,
};

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setRowCount: (state, action) => {
      state.rowCount = action.payload.rowCount;
      state.amountInvested = action.payload.amountInvested;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateLocalItem: (state, action) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch portfolio data
      .addCase(fetchPortfolioDataAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolioDataAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        state.rowCount = action.payload.totalrecords || 0;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPortfolioDataAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch portfolio data';
      })
      
      // Create portfolio item
      .addCase(createPortfolioItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPortfolioItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.rowCount += 1;
      })
      .addCase(createPortfolioItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create portfolio item';
      })
      
      // Update portfolio item
      .addCase(updatePortfolioItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePortfolioItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updatePortfolioItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update portfolio item';
      })
      
      // Delete portfolio item
      .addCase(deletePortfolioItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePortfolioItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload.id);
        state.rowCount -= 1;
      })
      .addCase(deletePortfolioItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete portfolio item';
      });
  },
});

export const { setRowCount, clearError, updateLocalItem } = portfolioSlice.actions;

// Selectors
export const selectPortfolioItems = (state) => state.portfolio.items;
export const selectPortfolioLoading = (state) => state.portfolio.loading;
export const selectPortfolioError = (state) => state.portfolio.error;
export const selectPortfolioCount = (state) => state.portfolio.rowCount;
export const selectAmountInvested = (state) => state.portfolio.amountInvested;

export default portfolioSlice.reducer;